import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { Modal } from '@shared/ui/Modal/Modal';

import { AuthDialogContext, type AuthDialogContextValue } from '../model/authDialogContext';
import { clearPendingAction, savePendingAction, takePendingAction } from '../model/pendingAction';

import { AuthFlow } from './AuthFlow';

/** Пауза перед закрытием: подтверждение входа должно быть замечено. */
const SUCCESS_DWELL_MS = 900;

/**
 * Окно входа, доступное из любой точки приложения.
 *
 * Один экземпляр на приложение: иначе каждая кнопка «в избранное» тащила бы за
 * собой собственную модалку с собственным состоянием.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const titleId = useId();
  const [open, setOpen] = useState(false);

  // Текущий адрес на момент срабатывания таймера возврата. В замыкании
  // `handleSuccess` лежит адрес времени входа, а решение принимается через
  // `SUCCESS_DWELL_MS` — за это время приложение успевает уйти дальше.
  const currentUrl = `${location.pathname}${location.search}`;
  const currentUrlRef = useRef(currentUrl);
  currentUrlRef.current = currentUrl;

  /**
   * Идентификатор паузы перед закрытием — чтобы её можно было отменить.
   *
   * Отмена обязательна: за 900 мс приложение успевает уйти со страницы и
   * размонтировать провайдер вместе с окном. Несброшенный таймер тогда
   * досчитывает в пустоту и трогает то, чего уже нет, — состояние снятого
   * компонента и роутер, которого больше не существует.
   */
  const dwellTimer = useRef<number | null>(null);
  const clearDwell = useCallback(() => {
    if (dwellTimer.current !== null) {
      window.clearTimeout(dwellTimer.current);
      dwellTimer.current = null;
    }
  }, []);
  useEffect(() => clearDwell, [clearDwell]);

  const requestAuth = useCallback<AuthDialogContextValue['requestAuth']>(
    (pending) => {
      if (pending) {
        savePendingAction({
          ...pending,
          // По умолчанию возвращаем туда, откуда пришли.
          returnUrl: pending.returnUrl ?? `${location.pathname}${location.search}`,
        });
      } else {
        clearPendingAction();
      }
      setOpen(true);
    },
    [location.pathname, location.search],
  );

  const closeAuth = useCallback(() => {
    setOpen(false);
  }, []);

  /**
   * Успешный вход: закрыть окно и вернуть пользователя туда, откуда он пришёл.
   *
   * Возврат выполняется, **только если приложение за время паузы никуда не
   * ушло само**. Закрытый экран, открывшийся сразу после входа, вправе сделать
   * следующий шаг за пользователя: список запросов создаёт подбор из текста с
   * главной и открывает диалог (AC-01). Безусловный переход на `returnUrl`
   * отменял бы этот шаг и возвращал пользователя на страницу, которую он уже
   * прошёл, — ровно то, что выглядит как «запрос потерялся».
   *
   * Порядок при этом не важен: если создание запроса окажется медленнее паузы,
   * возврат на `returnUrl` сработает как переход на ту же страницу, а переход
   * в диалог случится следом.
   */
  const handleSuccess = useCallback(() => {
    const pending = takePendingAction();
    clearDwell();
    dwellTimer.current = window.setTimeout(() => {
      dwellTimer.current = null;
      setOpen(false);
      if (pending && currentUrlRef.current === pending.returnUrl) {
        void navigate(pending.returnUrl);
      }
    }, SUCCESS_DWELL_MS);
  }, [clearDwell, navigate]);

  const value = useMemo<AuthDialogContextValue>(
    () => ({ requestAuth, closeAuth, isOpen: open }),
    [requestAuth, closeAuth, open],
  );

  return (
    <AuthDialogContext.Provider value={value}>
      {children}
      <Modal open={open} onClose={closeAuth} labelledBy={titleId}>
        {/* Поток монтируется только на время показа: чужой адрес и старый код
            не должны всплыть при следующем открытии. */}
        {open ? <AuthFlow titleId={titleId} onSuccess={handleSuccess} /> : null}
      </Modal>
    </AuthDialogContext.Provider>
  );
}
